import React, { useCallback } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import { Field } from '@atlaskit/form';
import { Flex, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

// This file is based on confluence/next/packages/object-sidebar-components/src/sidebar-buttons/FeedbackCollectorButton.tsx

const styles = cssMap({
	categoryPillGroupContainer: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-start',
		gap: token('space.100'),
		flexWrap: 'wrap',

		marginBottom: token('space.200'),
	},
	categoryPill: {
		height: '32px',
		justifyContent: 'center',
		alignItems: 'center',

		userSelect: 'none',
		whiteSpace: 'nowrap',
		boxSizing: 'border-box',
		font: token('font.body'),
		color: token('color.text'),
		backgroundColor: token('color.background.neutral'),

		paddingTop: token('space.075'),
		paddingBottom: token('space.075'),
		paddingRight: token('space.150'),
		paddingLeft: token('space.150'),

		borderColor: 'transparent',
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderRadius: token('radius.full'),

		'&:hover': {
			backgroundColor: token('color.background.neutral.hovered'),
		},
	},
	categoryPillSelected: {
		color: token('color.text.selected'),
		backgroundColor: token('color.background.selected'),

		borderColor: token('color.border.selected'),

		'&:hover': {
			backgroundColor: token('color.background.selected.hovered'),
		},
	},
});

export type Category = {
	// A language-agnostic string identifier for the category
	id: string;

	// A properly localized string for the category
	text: string;
};

export type CategoryPillGroupProps = {
	categories: Category[];
	selectedCategory: string | undefined;
	setSelectedCategory: React.Dispatch<React.SetStateAction<string | undefined>>;
	label: string;
};

export const CategoryPillGroup = ({
	categories,
	selectedCategory,
	setSelectedCategory,
	label,
}: CategoryPillGroupProps) => {
	const onCategoryButtonClick = useCallback(
		(category: string) => (e: React.MouseEvent<HTMLButtonElement>) => {
			e.preventDefault();
			setSelectedCategory(category);
		},
		[setSelectedCategory],
	);

	return (
		<Field label={label} isRequired={false} name="category-group" id="category-group">
			{({ fieldProps }) => (
				<>
					<Flex
						data-name={fieldProps.id}
						xcss={styles.categoryPillGroupContainer}
						role="radiogroup"
					>
						{categories.map((category) => (
							<Pressable
								role="radio"
								aria-checked={category.id === selectedCategory}
								key={category.id}
								data-name={category.text}
								data-value={category.id}
								onClick={onCategoryButtonClick(category.id)}
								xcss={cx(
									styles.categoryPill,
									category.id === selectedCategory && styles.categoryPillSelected,
								)}
							>
								{category.text}
							</Pressable>
						))}
					</Flex>
				</>
			)}
		</Field>
	);
};
